import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Select,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBathrooms,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_squareFeet,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { getSquareFeetError } from '../../../utils/formValidation';

import { bathOptions, bedOptions } from '../../../constants/ListingDetailsConstants';

import SkeletonPropertyDetailsModal from './components/SkeletonPropertyDetailsModal';

type Props = {
  isOpen: boolean;
  floorPlanId?: string;
  listingAlias?: string;
  submitLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
};

const mutationMeta = {
  invalidates: ['listingDetails', 'getPropertyTableUnits', 'getPropertyTableUnitsList', 'getProperties'],
  context: { api: 'rental-manager', details: 'PropertyDetailsModal' },
};

const logPropertyDetailsError = (error: unknown, context: string) => {
  logError({
    error,
    errorType: 'PropertyDetailsModalError',
    context,
  });
};

const PropertyDetailsModal = ({
  isOpen,
  floorPlanId,
  listingAlias = '',
  submitLabel = 'Continue',
  onClose,
  onConfirm,
}: Props): JSX.Element => {
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [size, setSize] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const pendingConfirm = useRef(false);
  const onConfirmRef = useRef(onConfirm);
  const hydratedFloorPlanKey = useRef('');

  const { data: listingDetails, isPending: isListingDetailsPending } = useGetListingDetails(listingAlias);

  const isEditMode = Boolean(listingAlias);
  const listingFloorPlan = listingDetails?.floorPlans?.[0];
  const resolvedFloorPlanId = floorPlanId ?? listingFloorPlan?.id;
  const isLoadingDetails = isEditMode && isListingDetailsPending;

  useEffect(() => {
    if (!listingAlias || !listingFloorPlan?.id) {
      return;
    }

    const floorPlanKey = `${listingAlias}:${listingFloorPlan.id}`;
    if (hydratedFloorPlanKey.current === floorPlanKey) {
      return;
    }

    setBeds(String(listingFloorPlan.numBedrooms ?? ''));
    setBaths(String(listingFloorPlan.numBathrooms ?? ''));
    setSize(String(listingFloorPlan.squareFeet ?? ''));
    hydratedFloorPlanKey.current = floorPlanKey;
  }, [listingAlias, listingFloorPlan]);

  useLayoutEffect(() => {
    onConfirmRef.current = onConfirm;
  });

  const { enqueueToast } = useToast();

  const {
    mutate: updateBedrooms,
    mutateAsync: updateBedroomsAsync,
    status: bedsSaveState,
  } = useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms({
    meta: mutationMeta,
    onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateBedrooms'),
  });
  const {
    mutate: updateBathrooms,
    mutateAsync: updateBathroomsAsync,
    status: bathsSaveState,
  } = useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBathrooms({
    meta: mutationMeta,
    onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateBathrooms'),
  });
  const {
    mutate: updateSquareFeet,
    mutateAsync: updateSquareFeetAsync,
    status: sizeSaveState,
    reset: resetSquareFeet,
  } = useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_squareFeet({
    meta: mutationMeta,
    onError: (error) => logPropertyDetailsError(error, 'PropertyDetailsModal.updateSquareFeet'),
  });

  const showBedsError = hasSubmitted && !beds;
  const showBathsError = hasSubmitted && !baths;
  const sizeError = getSquareFeetError(size);
  const sizeValue = size === '' ? null : Number(size);
  const showLargeSizeWarning = !sizeError && sizeValue !== null && !Number.isNaN(sizeValue) && sizeValue >= 20000;
  const sizeFieldMessage =
    sizeError ??
    (showLargeSizeWarning
      ? 'Square footage values of 20,000 or more are unusually large. Verify this is correct.'
      : null);
  const isSaving = bedsSaveState === 'pending' || bathsSaveState === 'pending' || sizeSaveState === 'pending';
  const hasFieldSaveError = bedsSaveState === 'error' || bathsSaveState === 'error' || sizeSaveState === 'error';

  useEffect(() => {
    if (!isOpen) {
      pendingConfirm.current = false;
      return;
    }
    if (!pendingConfirm.current || isSaving) return;
    pendingConfirm.current = false;
    if (!hasFieldSaveError && beds && baths && resolvedFloorPlanId && !sizeError) {
      onConfirmRef.current();
    }
  }, [isSaving, hasFieldSaveError, isOpen, beds, baths, resolvedFloorPlanId, sizeError]);

  const handleBedsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    setBeds(value);
    setSubmitError(false);
    if (isEditMode || !value || !resolvedFloorPlanId) return;
    updateBedrooms({ postData: { floorPlanId: resolvedFloorPlanId, numBedrooms: Number(value) } });
  };

  const handleBathsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    setBaths(value);
    setSubmitError(false);

    if (isEditMode || !value || !resolvedFloorPlanId) return;
    updateBathrooms({ postData: { floorPlanId: resolvedFloorPlanId, numBathrooms: Number(value) } });
  };

  const handleSizeBlur = () => {
    if (isEditMode || !size || !resolvedFloorPlanId || sizeError) return;
    updateSquareFeet({ postData: { floorPlanId: resolvedFloorPlanId, squareFeet: Number(size) } });
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!beds || !baths || !resolvedFloorPlanId || sizeError) return;

    if (!isEditMode) {
      if (isSaving) {
        pendingConfirm.current = true;
        return;
      }
      if (hasFieldSaveError) return;
      onConfirm();
      return;
    }

    setSubmitError(false);
    try {
      const saves: Array<Promise<unknown>> = [
        updateBedroomsAsync({ postData: { floorPlanId: resolvedFloorPlanId, numBedrooms: Number(beds) } }),
        updateBathroomsAsync({ postData: { floorPlanId: resolvedFloorPlanId, numBathrooms: Number(baths) } }),
      ];
      if (size) {
        saves.push(updateSquareFeetAsync({ postData: { floorPlanId: resolvedFloorPlanId, squareFeet: Number(size) } }));
      }
      await Promise.all(saves);
      onConfirm();
    } catch {
      setSubmitError(true);
      enqueueToast(<Toast tone="critical">Changes couldn’t be saved. Try again.</Toast>);
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      header={<Heading level={2}>Tell us about your property</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <Text>
              Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
              perfect renter.
            </Text>
          </Box>

          {isLoadingDetails ? (
            <SkeletonPropertyDetailsModal />
          ) : (
            <>
              {!isEditMode && bedsSaveState === 'error' && (
                <Alert tone="critical">Beds could not be saved. Select a value to retry.</Alert>
              )}
              {!isEditMode && bathsSaveState === 'error' && (
                <Alert tone="critical">Baths could not be saved. Select a value to retry.</Alert>
              )}
              {!isEditMode && sizeSaveState === 'error' && (
                <Alert tone="critical">Square footage could not be saved. Re-enter the value to retry.</Alert>
              )}
              {submitError && <Alert tone="critical">Changes could not be saved. Try again.</Alert>}

              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                <FormField
                  required
                  error={showBedsError}
                  label={<Label>Beds</Label>}
                  control={
                    <Select data-testid="beds-select" value={beds} onChange={handleBedsChange}>
                      {bedOptions.map((opt: string) => {
                        if (opt === 'studio') {
                          return (
                            <option key={opt} value={0}>
                              {opt}
                            </option>
                          );
                        }
                        return (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        );
                      })}
                    </Select>
                  }
                  description={showBedsError ? <FormHelp>Number of beds is required</FormHelp> : undefined}
                />

                <FormField
                  required
                  error={showBathsError}
                  label={<Label>Baths</Label>}
                  control={
                    <Select data-testid="baths-select" value={baths} onChange={handleBathsChange}>
                      {bathOptions.map((opt: string) => {
                        if (opt === '10+') {
                          return (
                            <option key={opt} value={10}>
                              {opt}
                            </option>
                          );
                        }
                        return (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        );
                      })}
                    </Select>
                  }
                  description={showBathsError ? <FormHelp>Number of bathrooms is required</FormHelp> : undefined}
                />

                <FormField
                  optional
                  error={Boolean(sizeFieldMessage)}
                  label={<Label>Size</Label>}
                  control={
                    <AdornedInput
                      input={
                        <AdornedInput.Input
                          data-testid="size-input"
                          type="text"
                          inputMode="numeric"
                          value={size}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSize(val);
                            setSubmitError(false);
                            if (!val && sizeSaveState === 'error') resetSquareFeet();
                          }}
                          onBlur={handleSizeBlur}
                        />
                      }
                      endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
                    />
                  }
                  description={sizeFieldMessage ? <FormHelp>{sizeFieldMessage}</FormHelp> : undefined}
                />
              </Box>
            </>
          )}
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Property details actions">
          <Button fluid={false} tone="brand" emphasis="bare" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="property-details-continue-button"
            fluid={false}
            tone="brand"
            emphasis="filled"
            loading={isEditMode && isSaving}
            disabled={isLoadingDetails || Boolean(sizeError)}
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default PropertyDetailsModal;
